//src/components/layout/AppLayout.tsx

import { useEffect, useMemo, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { Topbar } from './Topbar';
import { Skeleton } from '@/components/ui/skeleton';
import { OperationalClientRequired } from '@/components/shared/OperationalClientRequired';
import { useClientCompanyContext } from '@/hooks/useClientCompanyContext';
import { useQueryClient } from '@tanstack/react-query';
import ClientCompanySelectionPage from '@/pages/ClientCompanySelectionPage';

const TENANT_CONTEXT_ROUTE_PREFIXES = [
  '/cadastros/empresa',
  '/cadastros/clientes',
  '/cadastros/grupos',
  '/cadastros/usuarios',
  '/configuracoes',
  '/portal-empresa',
  '/portal-consultoria',
  '/tenant',
  '/financeiro',
];

function isTenantContextRoute(pathname: string) {
  return TENANT_CONTEXT_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function AppLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    hasSelectedClientCompany,
    isLoadingClientCompanies,
    isTenantAdmin,
    selectedClientCompanyId,
  } = useClientCompanyContext();

  const currentContextKey = selectedClientCompanyId ?? 'tenant';
  const [readyContextKey, setReadyContextKey] = useState(currentContextKey);

  const preserveQuery = useMemo(
    () => (queryKey: readonly unknown[]) =>
      queryKey[0] === 'client_companies' && queryKey[1] === 'available',
    [],
  );

  useEffect(() => {
    if (readyContextKey === currentContextKey) return;

    let active = true;

    const resetClientScopedCache = async () => {
      await queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: (query) => !preserveQuery(query.queryKey),
      });

      if (active) {
        setReadyContextKey(currentContextKey);
      }
    };

    void resetClientScopedCache();

    return () => {
      active = false;
    };
  }, [currentContextKey, preserveQuery, queryClient, readyContextKey]);

  // Skeleton de carregamento inicial
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  // Redirecionar para login se não autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isRootRoute = location.pathname === '/';
  const isClientContextRoute = !isRootRoute && !isTenantContextRoute(location.pathname);

  const isContextTransitioning = readyContextKey !== currentContextKey;

  let content = isContextTransitioning ? (
    <div className="space-y-4" aria-label="Atualizando contexto do cliente">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  ) : (
    <Outlet key={currentContextKey} />
  );

  if (!hasSelectedClientCompany && !isTenantAdmin && !isLoadingClientCompanies) {
    content = <ClientCompanySelectionPage />;
  } else if (!hasSelectedClientCompany && !isTenantAdmin && isLoadingClientCompanies) {
    content = null;
  } else if (!hasSelectedClientCompany && isClientContextRoute) {
    content = <OperationalClientRequired />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <Topbar />
          <main className="flex-1 p-6 animate-fade-in">
            {content}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
